import React, { useState } from 'react';
import ThankYouPopup from './ThankYouPopup';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessName: string;
  website: string;
  country: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    website: '',
    country: '',
  });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    const {
      firstName,
      lastName,
      email,
      phone,
      businessName,
      website,
      country,
    } = formData;
  
    const fullName = `${firstName} ${lastName}`;
  
    // Log all environment variables to debug
    console.log("Environment variables:", process.env);
    
    // Try to get the API key from environment variables, or use a fallback mechanism
    // Note: In production, always use environment variables instead of hardcoded values
    const mondayApiKey = process.env.REACT_APP_MONDAY_API_KEY;
    
    if (!mondayApiKey) {
      console.error("Monday.com API key is not defined. Please check your environment configuration.");
      setErrorMessage("Configuration error: API key is missing. Please contact support.");
      setIsLoading(false);
      return;
    }
  
    const query = `
      mutation {
        create_item(
          board_id: 9146428714,
          item_name: "${fullName}",
          column_values: "${JSON.stringify({
            lead_email: {
              email: email,
              text: email
            },
            lead_phone: {
              phone: phone,
              countryShortName: country
            },
            lead_company: businessName,
            link_mkqykpx: {
              url: website,
              text: website.replace(/^https?:\/\//, '')
            },
            location: {
              address: country,
              lat: 37.0902, // default US center
              lng: -95.7129
            }
          }).replace(/"/g, '\\"')}"
        ) {
          id
        }
      }
    `;
  
    try {
      const response = await fetch("https://api.monday.com/v2", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${mondayApiKey}`
        },
        body: JSON.stringify({ query })
      });
  
      const result = await response.json();
      console.log("Monday response:", result);
      setIsLoading(false);
  
      if (result.errors) {
        console.error("Monday API Error:", result.errors);
        setErrorMessage("There was an error submitting the form. Please try again.");
      } else {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          businessName: '',
          website: '',
          country: '',
        });
        setIsPopupOpen(true);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setIsLoading(false);
      setErrorMessage("Something went wrong. Please check your connection and try again.");
    }
  };
  

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
             IgniLead
          </h1>
        </div>
        
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded-md">
            {errorMessage}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter Your First Name Here*"
              required
              className="w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter Your Last Name Here*"
              required
              className="w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter Your Email Here*"
              required
              className="w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter Your Phone Number Here*"
              required
              className="w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              placeholder="Your Business Name"
              className="w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="Your Website*"
              required
              className="w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-md bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Country*</option>
              <option value="US">United States</option>
              <option value="UK">United Kingdom</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
              {/* Add more countries as needed */}
            </select>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-4 px-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-md hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transform transition-all duration-200 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? 'Submitting...' : 'Apply To Become A Client'}
          </button>
        </form>
      </div>
      <ThankYouPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default Contact; 